import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { AntDesign, Entypo, MaterialIcons } from "@expo/vector-icons";

import UserAvatar from "@/Components/Media/User/Images/UserAvatar";
import { formatDistanceStr } from "@/formatting/distance";
import { type AppUserSession } from "@/schemas/auth/session";
import PermissionService, {
  type PermissionRequest,
  RequestState,
} from "@/services/user/permission";

interface MediaRequestCardProps {
  session: AppUserSession;
  request: PermissionRequest;
  removeItem: (id: string) => void;
}

function MediaRequestCard({
  session,
  request,
  removeItem,
}: MediaRequestCardProps): React.JSX.Element {
  const granteeProfilePreview = request.grantee;
  const separation = formatDistanceStr(granteeProfilePreview.separation);

  const handleActionPress = (
    request: PermissionRequest,
    newStatus: RequestState,
  ): void => {
    const permsService = new PermissionService(session);
    permsService.updateRequest(request.id, newStatus);
    removeItem(request.id);
  };

  return (
    <View style={styles.container}>
      <UserAvatar
        defaultAvatarColor={"black"}
        avatar={granteeProfilePreview.avatar}
        size={80}
        profilePress={{
          username: granteeProfilePreview.username,
        }}
      />
      <View style={styles.infoContainer}>
        <View style={styles.detailItem}>
          <AntDesign name="user" size={20} color="blue" />
          <Text style={styles.detailText}>
            {granteeProfilePreview.username}
          </Text>
        </View>
        <View style={styles.detailItem}>
          <Entypo name="location" size={20} color="#ff6100" />
          <Text style={styles.detailText}>{separation}</Text>
        </View>
        <View style={styles.connectionsItem}>
          <View style={styles.detailItem}>
            <MaterialIcons name="people" size={20} color="green" />
            <Text style={styles.detailText}>
              {granteeProfilePreview.followerCount}
            </Text>
          </View>
          <View style={[styles.detailItem, { marginLeft: 10 }]}>
            <MaterialIcons name="person-add" size={20} color="green" />
            <Text style={styles.detailText}>
              {granteeProfilePreview.followingCount}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.buttonsContainer}>
        {request.status === RequestState.INIT && (
          <>
            <TouchableOpacity
              style={styles.approveButton}
              onPress={() => handleActionPress(request, RequestState.APPROVED)}
            >
              <Text style={styles.buttonText}>Approve</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.rejectButton}
              onPress={() => handleActionPress(request, RequestState.REJECTED)}
            >
              <Text style={styles.buttonText}>Deny</Text>
            </TouchableOpacity>
          </>
        )}
        {request.status === RequestState.APPROVED && (
          <>
            <TouchableOpacity
              style={styles.rejectButton}
              onPress={() => handleActionPress(request, RequestState.REJECTED)}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  approveButton: {
    alignContent: "center",
    alignItems: "center",
    backgroundColor: "#4CAF50",
    borderRadius: 20,
    flexDirection: "row",
    justifyContent: "center",
    marginRight: 10,
    padding: 10,
    width: 80,
  },
  buttonText: {
    color: "white",
  },
  buttonsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  connectionsItem: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 5,
  },
  container: {
    alignItems: "center",
    flexDirection: "row",
    padding: 8,
  },
  detailItem: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 3,
  },
  detailText: {
    color: "black",
    marginLeft: 5,
  },
  infoContainer: {
    flex: 1,
    marginLeft: 10,
    marginTop: 5,
  },
  rejectButton: {
    alignContent: "center",
    alignItems: "center",
    backgroundColor: "#F44336",
    borderRadius: 20,
    flexDirection: "row",
    justifyContent: "center",
    padding: 10,
    width: 80,
  },
});

export default MediaRequestCard;
